import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

import { GOOGLE_MAPS_ENABLED } from "../config/googleMaps";
import { primeGlobalSpeechSynthesis } from "../utils/audio/speechPrimer";
import { trackEvent } from "../utils/analytics/track";
import {
  buildPostRecordingResumeState,
  getPostRecordingResumeStartAt,
  parseTimestampMs,
} from "../utils/recording/sixMinuteWalkAssessment";
import {
  MAX_ACCEPTED_ACCURACY_METERS,
  MAX_ACCEPTED_SPEED_METRES_PER_SECOND,
  RETURN_TO_START_THRESHOLD_METERS,
  SIX_MINUTE_WALK_BASE_PATH,
  WALK_DURATION_SECONDS,
  WALK_TURN_AROUND_SECONDS,
} from "../utils/recording/sixMinuteWalkConfig";
import {
  appendGeoRouteTracePoint,
  appendRouteTracePoint,
  buildGeoRouteTracePoint,
  buildGeoTrackingPoint,
  buildRouteTracePoint,
  compressRouteTrace,
  computeWalkProgressPercent,
  formatDistanceLabel,
  formatDurationLabel,
  haversineDistanceMeters,
  logSixMinuteWalkFlowDebug,
  readFiniteNumber,
  roundTo,
  speakPrompt,
} from "../utils/recording/sixMinuteWalkTracking";

export function useSixMinuteWalkTracking({
  assessment,
  setAssessment,
  audioMuted = false,
  resumePostRecording = false,
  onContinueToPost = null,
  midpointPrompt,
  promptLanguage,
}) {
  const navigate = useNavigate();
  const [status, setStatus] = useState("idle");
  const [elapsedSeconds, setElapsedSeconds] = useState(0);
  const [distanceMeters, setDistanceMeters] = useState(0);
  const [sixMinuteDistanceMeters, setSixMinuteDistanceMeters] = useState(null);
  const [distanceFromStartMeters, setDistanceFromStartMeters] = useState(0);
  const [hasReturnedToStart, setHasReturnedToStart] = useState(false);
  const [locationError, setLocationError] = useState(null);
  const [routeTracePreview, setRouteTracePreview] = useState([]);
  const [geoRouteTracePreview, setGeoRouteTracePreview] = useState([]);
  const watchIdRef = useRef(null);
  const intervalRef = useRef(null);
  const startTimeRef = useRef(null);
  const startPointRef = useRef(null);
  const lastPointRef = useRef(null);
  const distanceMetersRef = useRef(0);
  const distanceFromStartMetersRef = useRef(0);
  const sixMinuteDistanceMetersRef = useRef(null);
  const sampleCountRef = useRef(0);
  const midpointPromptedRef = useRef(false);
  const timedCompleteRef = useRef(false);
  const sixMinuteCompletedAtRef = useRef(null);
  const returnedAtRef = useRef(null);
  const wakeLockRef = useRef(null);
  const routeTraceRef = useRef([]);
  const geoRouteTraceRef = useRef([]);
  const rejectedLocationSampleCountRef = useRef(0);
  const discardedSpeedSampleCountRef = useRef(0);
  const lastLiveWalkDebugStateRef = useRef("");
  const resumeTimeToRecordingStartedAtMs = parseTimestampMs(
    getPostRecordingResumeStartAt(assessment),
  );
  const [resumeElapsedSeconds, setResumeElapsedSeconds] = useState(() => {
    if (
      !resumePostRecording ||
      !Number.isFinite(resumeTimeToRecordingStartedAtMs)
    ) {
      return 0;
    }

    return Math.max(
      0,
      Math.floor((Date.now() - resumeTimeToRecordingStartedAtMs) / 1000),
    );
  });

  const releaseWakeLock = useCallback(async () => {
    const wakeLock = wakeLockRef.current;
    wakeLockRef.current = null;

    if (!wakeLock || typeof wakeLock.release !== "function") {
      return;
    }

    try {
      await wakeLock.release();
    } catch {}
  }, []);

  const requestWakeLock = useCallback(async () => {
    if (
      typeof document === "undefined" ||
      document.visibilityState === "hidden" ||
      typeof navigator === "undefined" ||
      !navigator.wakeLock ||
      typeof navigator.wakeLock.request !== "function"
    ) {
      return;
    }

    if (wakeLockRef.current && !wakeLockRef.current.released) {
      return;
    }

    try {
      const wakeLock = await navigator.wakeLock.request("screen");
      wakeLockRef.current = wakeLock;
      wakeLock?.addEventListener?.("release", () => {
        if (wakeLockRef.current === wakeLock) {
          wakeLockRef.current = null;
        }
      });
    } catch {}
  }, []);

  const clearTracking = useCallback(() => {
    if (
      watchIdRef.current != null &&
      typeof navigator !== "undefined" &&
      navigator.geolocation
    ) {
      navigator.geolocation.clearWatch(watchIdRef.current);
    }
    watchIdRef.current = null;

    if (intervalRef.current != null) {
      window.clearInterval(intervalRef.current);
    }
    intervalRef.current = null;
  }, []);

  const appendLiveRouteTracePoint = useCallback((point, options = {}) => {
    appendRouteTracePoint(routeTraceRef, point, options);
    setRouteTracePreview([...routeTraceRef.current]);
  }, []);

  const appendLiveGeoRouteTracePoint = useCallback((point, options = {}) => {
    appendGeoRouteTracePoint(geoRouteTraceRef, point, options);
    setGeoRouteTracePreview([...geoRouteTraceRef.current]);
  }, []);

  useEffect(() => {
    if (!resumePostRecording || !assessment?.walk) {
      return undefined;
    }

    const walkSummary = assessment.walk;
    const restoredElapsedMs =
      readFiniteNumber(walkSummary?.elapsedMs) ?? WALK_DURATION_SECONDS * 1000;
    const restoredDistanceMeters =
      readFiniteNumber(walkSummary?.totalDistanceMeters) ?? 0;
    const restoredTimedDistanceMeters =
      readFiniteNumber(walkSummary?.sixMinuteDistanceMeters) ??
      restoredDistanceMeters;
    const restoredDistanceFromStartMeters =
      readFiniteNumber(walkSummary?.distanceFromStartMeters) ?? 0;
    const restoredReturnedAt =
      assessment?.returnedAt || walkSummary?.returnedAt || null;

    setStatus("timed-complete");
    setElapsedSeconds(Math.max(0, Math.floor(restoredElapsedMs / 1000)));
    setDistanceMeters(restoredDistanceMeters);
    setSixMinuteDistanceMeters(restoredTimedDistanceMeters);
    setDistanceFromStartMeters(restoredDistanceFromStartMeters);
    setHasReturnedToStart(Boolean(restoredReturnedAt));
    setLocationError(null);

    distanceMetersRef.current = restoredDistanceMeters;
    sixMinuteDistanceMetersRef.current = restoredTimedDistanceMeters;
    distanceFromStartMetersRef.current = restoredDistanceFromStartMeters;
    sampleCountRef.current = readFiniteNumber(walkSummary?.sampleCount) ?? 0;
    timedCompleteRef.current = true;
    sixMinuteCompletedAtRef.current = walkSummary?.completedAt || null;
    returnedAtRef.current = restoredReturnedAt;
    routeTraceRef.current = Array.isArray(walkSummary?.routeTrace)
      ? walkSummary.routeTrace
      : [];
    geoRouteTraceRef.current = Array.isArray(walkSummary?.geoRouteTrace)
      ? walkSummary.geoRouteTrace
      : [];
    setRouteTracePreview([...routeTraceRef.current]);
    setGeoRouteTracePreview([...geoRouteTraceRef.current]);
    logSixMinuteWalkFlowDebug(
      "info",
      "Restored saved walk summary for resume.",
      {
        assessmentId: assessment?.assessmentId || null,
        routeTracePointCount: routeTraceRef.current.length,
        geoRoutePointCount: geoRouteTraceRef.current.length,
        totalDistanceMeters: restoredDistanceMeters,
        sixMinuteDistanceMeters: restoredTimedDistanceMeters,
        returnedAt: restoredReturnedAt,
      },
    );

    if (!Number.isFinite(resumeTimeToRecordingStartedAtMs)) {
      setResumeElapsedSeconds(0);
      return undefined;
    }

    const updateResumeElapsed = () => {
      setResumeElapsedSeconds(
        Math.max(
          0,
          Math.floor((Date.now() - resumeTimeToRecordingStartedAtMs) / 1000),
        ),
      );
    };

    updateResumeElapsed();
    const resumeIntervalId = window.setInterval(updateResumeElapsed, 250);

    return () => {
      window.clearInterval(resumeIntervalId);
    };
  }, [assessment, resumePostRecording, resumeTimeToRecordingStartedAtMs]);

  const finishTimedWalk = useCallback(() => {
    if (timedCompleteRef.current || !startTimeRef.current) {
      return;
    }

    timedCompleteRef.current = true;
    const completedAt = new Date(
      startTimeRef.current + WALK_DURATION_SECONDS * 1000,
    ).toISOString();
    const timedDistance = distanceMetersRef.current;

    sixMinuteCompletedAtRef.current = completedAt;
    sixMinuteDistanceMetersRef.current = timedDistance;
    setSixMinuteDistanceMeters(timedDistance);
    setStatus("timed-complete");

    if (
      !returnedAtRef.current &&
      distanceFromStartMetersRef.current <= RETURN_TO_START_THRESHOLD_METERS
    ) {
      returnedAtRef.current = completedAt;
      setHasReturnedToStart(true);
    }

    trackEvent("six_minute_walk_completed", {
      assessmentId: assessment?.assessmentId || null,
      distanceMeters: timedDistance,
      elapsedSeconds: WALK_DURATION_SECONDS,
    });
  }, [assessment?.assessmentId]);

  const proceedToPostRecording = useCallback(() => {
    if (resumePostRecording) {
      onContinueToPost?.();
      return;
    }

    if (!startTimeRef.current || !timedCompleteRef.current) {
      return;
    }

    if (startPointRef.current && lastPointRef.current) {
      appendLiveRouteTracePoint(
        buildRouteTracePoint(
          startPointRef.current,
          lastPointRef.current,
          distanceMetersRef.current,
        ),
        { force: true },
      );
      appendLiveGeoRouteTracePoint(
        buildGeoRouteTracePoint(
          startPointRef.current,
          lastPointRef.current,
          distanceMetersRef.current,
        ),
        { force: true },
      );
    }

    clearTracking();

    const startedAt = new Date(startTimeRef.current);
    const trackingStoppedAt = new Date();
    const timedCompletedAt =
      sixMinuteCompletedAtRef.current ||
      new Date(
        startTimeRef.current + WALK_DURATION_SECONDS * 1000,
      ).toISOString();
    const timedDistance =
      sixMinuteDistanceMetersRef.current ?? distanceMetersRef.current;
    const returnedAt =
      returnedAtRef.current ||
      (distanceFromStartMetersRef.current <= RETURN_TO_START_THRESHOLD_METERS
        ? trackingStoppedAt.toISOString()
        : null);
    const routeTrace = compressRouteTrace(routeTraceRef.current);
    const geoRouteTrace = compressRouteTrace(geoRouteTraceRef.current);
    const continuedAt = trackingStoppedAt.toISOString();
    const walkSummary = {
      startedAt: startedAt.toISOString(),
      completedAt: timedCompletedAt,
      elapsedMs: WALK_DURATION_SECONDS * 1000,
      sixMinuteDistanceMeters: timedDistance,
      totalDistanceMeters: distanceMetersRef.current,
      trackedElapsedMs: trackingStoppedAt.getTime() - startedAt.getTime(),
      trackingStoppedAt: trackingStoppedAt.toISOString(),
      distanceFromStartMeters: distanceFromStartMetersRef.current,
      sampleCount: sampleCountRef.current,
      turnAroundAtSeconds: WALK_TURN_AROUND_SECONDS,
      returnedAt,
      routeTrace,
      geoRouteTrace,
      routeTraceKind: routeTrace.length ? "relative-meters-v1" : null,
      geoRouteTraceKind: geoRouteTrace.length ? "wgs84-v1" : null,
    };
    logSixMinuteWalkFlowDebug("info", "Persisting completed walk summary.", {
      assessmentId: assessment?.assessmentId || null,
      routeTracePointCount: routeTrace.length,
      geoRoutePointCount: geoRouteTrace.length,
      totalDistanceMeters: distanceMetersRef.current,
      sixMinuteDistanceMeters: timedDistance,
      returnedAt,
      trackingStoppedAt: walkSummary.trackingStoppedAt,
    });
    const postResume = buildPostRecordingResumeState({
      completedAt: timedCompletedAt,
      trackingStoppedAt: trackingStoppedAt.toISOString(),
      returnedAt,
      continuedAt,
    });

    setAssessment((currentAssessment) => ({
      ...currentAssessment,
      walk: walkSummary,
      postResume,
      returnedAt,
    }));

    trackEvent("six_minute_walk_tracking_stopped", {
      assessmentId: assessment?.assessmentId || null,
      sixMinuteDistanceMeters: timedDistance,
      totalDistanceMeters: distanceMetersRef.current,
      returnedAt,
    });

    navigate(`${SIX_MINUTE_WALK_BASE_PATH}/post`, {
      replace: true,
      state: { autoStartPost: true },
    });
  }, [
    assessment?.assessmentId,
    appendLiveRouteTracePoint,
    appendLiveGeoRouteTracePoint,
    clearTracking,
    navigate,
    onContinueToPost,
    resumePostRecording,
    setAssessment,
  ]);

  const startWalk = useCallback(() => {
    logSixMinuteWalkFlowDebug("info", "Starting live geolocation tracking.", {
      assessmentId: assessment?.assessmentId || null,
      hasGeolocation: Boolean(
        typeof navigator !== "undefined" && navigator.geolocation,
      ),
      hasWatchPosition: Boolean(
        typeof navigator !== "undefined" &&
          navigator.geolocation &&
          typeof navigator.geolocation.watchPosition === "function",
      ),
    });

    if (
      typeof navigator === "undefined" ||
      !navigator.geolocation ||
      typeof navigator.geolocation.watchPosition !== "function"
    ) {
      logSixMinuteWalkFlowDebug(
        "warn",
        "Browser geolocation is unavailable for the live walk.",
      );
      setLocationError("unsupported");
      setStatus("error");
      return;
    }

    if (!audioMuted) {
      // iOS requires speech synthesis to be activated from a user gesture.
      // Starting the walk is the last guaranteed gesture before the delayed
      // three-minute turn-around instruction.
      primeGlobalSpeechSynthesis();
    }

    setLocationError(null);
    setStatus("walking");
    setElapsedSeconds(0);
    setDistanceMeters(0);
    setSixMinuteDistanceMeters(null);
    setDistanceFromStartMeters(0);
    setHasReturnedToStart(false);
    startPointRef.current = null;
    lastPointRef.current = null;
    distanceMetersRef.current = 0;
    distanceFromStartMetersRef.current = 0;
    sixMinuteDistanceMetersRef.current = null;
    sampleCountRef.current = 0;
    midpointPromptedRef.current = false;
    timedCompleteRef.current = false;
    sixMinuteCompletedAtRef.current = null;
    returnedAtRef.current = null;
    routeTraceRef.current = [];
    geoRouteTraceRef.current = [];
    rejectedLocationSampleCountRef.current = 0;
    discardedSpeedSampleCountRef.current = 0;
    lastLiveWalkDebugStateRef.current = "";
    if (status !== "idle" || routeTracePreview.length > 1) {
      setRouteTracePreview([]);
    }
    if (status !== "idle" || geoRouteTracePreview.length > 1) {
      setGeoRouteTracePreview([]);
    }
    startTimeRef.current = Date.now();

    trackEvent("six_minute_walk_started", {
      assessmentId: assessment?.assessmentId || null,
    });

    watchIdRef.current = navigator.geolocation.watchPosition(
      (position) => {
        const accuracy = Number(position?.coords?.accuracy);
        const nextPoint = buildGeoTrackingPoint(position);

        if (!nextPoint) {
          rejectedLocationSampleCountRef.current += 1;
          const rejectedSampleCount = rejectedLocationSampleCountRef.current;

          if (rejectedSampleCount <= 3 || rejectedSampleCount % 10 === 0) {
            logSixMinuteWalkFlowDebug(
              "warn",
              "Ignored live location sample because it was missing coordinates or too inaccurate.",
              {
                rejectedSampleCount,
                latitude: readFiniteNumber(position?.coords?.latitude),
                longitude: readFiniteNumber(position?.coords?.longitude),
                accuracyMeters: Number.isFinite(accuracy) ? accuracy : null,
                maxAcceptedAccuracyMeters: MAX_ACCEPTED_ACCURACY_METERS,
              },
            );
          }

          return;
        }

        sampleCountRef.current += 1;

        if (!lastPointRef.current || !startPointRef.current) {
          startPointRef.current = nextPoint;
          lastPointRef.current = nextPoint;
          appendLiveRouteTracePoint(
            buildRouteTracePoint(nextPoint, nextPoint, 0),
            { force: true },
          );
          appendLiveGeoRouteTracePoint(
            buildGeoRouteTracePoint(nextPoint, nextPoint, 0),
            { force: true },
          );
          setDistanceFromStartMeters(0);
          return;
        }

        const deltaSeconds =
          Math.max(1, nextPoint.timestamp - lastPointRef.current.timestamp) /
          1000;
        const segmentDistance = haversineDistanceMeters(
          lastPointRef.current,
          nextPoint,
        );
        const speed = segmentDistance / deltaSeconds;

        if (speed <= MAX_ACCEPTED_SPEED_METRES_PER_SECOND) {
          const nextDistanceMeters =
            distanceMetersRef.current + segmentDistance;
          distanceMetersRef.current = nextDistanceMeters;
          setDistanceMeters(nextDistanceMeters);
          lastPointRef.current = nextPoint;

          const nextDistanceFromStart = haversineDistanceMeters(
            startPointRef.current,
            nextPoint,
          );
          distanceFromStartMetersRef.current = nextDistanceFromStart;
          setDistanceFromStartMeters(nextDistanceFromStart);
          appendLiveRouteTracePoint(
            buildRouteTracePoint(
              startPointRef.current,
              nextPoint,
              nextDistanceMeters,
            ),
          );
          appendLiveGeoRouteTracePoint(
            buildGeoRouteTracePoint(
              startPointRef.current,
              nextPoint,
              nextDistanceMeters,
            ),
          );

          if (
            !returnedAtRef.current &&
            timedCompleteRef.current &&
            nextDistanceFromStart <= RETURN_TO_START_THRESHOLD_METERS
          ) {
            returnedAtRef.current = new Date(nextPoint.timestamp).toISOString();
            setHasReturnedToStart(true);
          }
        } else {
          discardedSpeedSampleCountRef.current += 1;
          const discardedSampleCount = discardedSpeedSampleCountRef.current;

          if (discardedSampleCount <= 3 || discardedSampleCount % 10 === 0) {
            logSixMinuteWalkFlowDebug(
              "warn",
              "Ignored live location sample because the inferred speed was too high.",
              {
                discardedSampleCount,
                segmentDistanceMeters: roundTo(segmentDistance, 1),
                deltaSeconds: roundTo(deltaSeconds, 2),
                speedMetresPerSecond: roundTo(speed, 2),
                maxAcceptedSpeedMetresPerSecond:
                  MAX_ACCEPTED_SPEED_METRES_PER_SECOND,
              },
            );
          }
        }
      },
      (error) => {
        logSixMinuteWalkFlowDebug("error", "Live geolocation watch failed.", {
          code: error?.code || null,
          message: error?.message || null,
          timedComplete: timedCompleteRef.current,
          routeTracePointCount: routeTraceRef.current.length,
          geoRoutePointCount: geoRouteTraceRef.current.length,
        });
        setLocationError(error?.code || "permission");
        clearTracking();
        setStatus(timedCompleteRef.current ? "timed-complete" : "error");
      },
      {
        enableHighAccuracy: true,
        maximumAge: 0,
        timeout: 15000,
      },
    );

    intervalRef.current = window.setInterval(() => {
      const startedAt = startTimeRef.current;
      if (!startedAt) {
        return;
      }

      const nextElapsedSeconds = Math.floor((Date.now() - startedAt) / 1000);

      setElapsedSeconds(nextElapsedSeconds);

      if (
        nextElapsedSeconds >= WALK_TURN_AROUND_SECONDS &&
        !midpointPromptedRef.current
      ) {
        midpointPromptedRef.current = true;
        speakPrompt({
          text: midpointPrompt,
          lang: promptLanguage,
          audioMuted,
        });
      }

      if (nextElapsedSeconds >= WALK_DURATION_SECONDS) {
        finishTimedWalk();
      }
    }, 250);
  }, [
    assessment?.assessmentId,
    appendLiveRouteTracePoint,
    appendLiveGeoRouteTracePoint,
    audioMuted,
    clearTracking,
    finishTimedWalk,
    geoRouteTracePreview.length,
    midpointPrompt,
    promptLanguage,
    routeTracePreview.length,
    status,
  ]);

  useEffect(() => {
    const routeTracePointCount = routeTracePreview.length;
    const geoRoutePointCount = geoRouteTracePreview.length;
    const stateKey = [
      status,
      routeTracePointCount,
      geoRoutePointCount,
      locationError || "ok",
      hasReturnedToStart ? "returned" : "not-returned",
    ].join("|");

    if (lastLiveWalkDebugStateRef.current === stateKey) {
      return;
    }

    lastLiveWalkDebugStateRef.current = stateKey;

    const isRoutineProgressUpdate =
      status === "walking" &&
      !locationError &&
      geoRoutePointCount > 3 &&
      geoRoutePointCount % 10 !== 0;

    if (isRoutineProgressUpdate) {
      return;
    }

    logSixMinuteWalkFlowDebug("info", "Live walk state changed.", {
      status,
      routeTracePointCount,
      geoRoutePointCount,
      distanceMeters: roundTo(distanceMeters, 1),
      distanceFromStartMeters: roundTo(distanceFromStartMeters, 1),
      hasReturnedToStart,
      locationError,
      lastGeoPoint: geoRouteTracePreview[geoRoutePointCount - 1] || null,
      lastRoutePoint: routeTracePreview[routeTracePointCount - 1] || null,
    });
  }, [
    distanceFromStartMeters,
    distanceMeters,
    geoRouteTracePreview,
    hasReturnedToStart,
    locationError,
    routeTracePreview,
    status,
  ]);

  useEffect(() => clearTracking, [clearTracking]);

  const hasPreMeasurement = Boolean(assessment?.preMeasurementId);
  const timedElapsedSeconds = Math.min(elapsedSeconds, WALK_DURATION_SECONDS);
  const progressPercent = computeWalkProgressPercent(timedElapsedSeconds);
  const isWalking = status === "walking";
  const isTimedComplete = status === "timed-complete";
  const hasStarted = isWalking || isTimedComplete;
  const isReturning = timedElapsedSeconds >= WALK_TURN_AROUND_SECONDS;
  const highlightedDistanceMeters = sixMinuteDistanceMeters ?? distanceMeters;
  const timerValue = resumePostRecording
    ? formatDurationLabel(resumeElapsedSeconds)
    : formatDurationLabel(
        Math.max(0, WALK_DURATION_SECONDS - timedElapsedSeconds),
      );
  const highlightedDistanceLabel = formatDistanceLabel(
    highlightedDistanceMeters,
  );

  useEffect(() => {
    if (
      !hasPreMeasurement ||
      hasStarted ||
      resumePostRecording ||
      !GOOGLE_MAPS_ENABLED
    ) {
      return undefined;
    }

    if (
      typeof navigator === "undefined" ||
      !navigator.geolocation ||
      typeof navigator.geolocation.getCurrentPosition !== "function"
    ) {
      logSixMinuteWalkFlowDebug(
        "warn",
        "Skipping pre-start location preview because browser geolocation is unavailable.",
      );
      return undefined;
    }

    let cancelled = false;
    logSixMinuteWalkFlowDebug("info", "Requesting pre-start location preview.", {
      assessmentId: assessment?.assessmentId || null,
    });

    navigator.geolocation.getCurrentPosition(
      (position) => {
        if (cancelled) {
          return;
        }

        const nextPoint = buildGeoTrackingPoint(position);

        if (!nextPoint) {
          logSixMinuteWalkFlowDebug(
            "warn",
            "Ignored pre-start location preview because it was missing coordinates or too inaccurate.",
            {
              accuracyMeters: readFiniteNumber(position?.coords?.accuracy),
              maxAcceptedAccuracyMeters: MAX_ACCEPTED_ACCURACY_METERS,
            },
          );
          return;
        }

        const routePreviewPoint = buildRouteTracePoint(nextPoint, nextPoint, 0);
        const geoPreviewPoint = buildGeoRouteTracePoint(nextPoint, nextPoint, 0);

        setLocationError(null);
        setRouteTracePreview(routePreviewPoint ? [routePreviewPoint] : []);
        setGeoRouteTracePreview(geoPreviewPoint ? [geoPreviewPoint] : []);

        logSixMinuteWalkFlowDebug("info", "Loaded pre-start location preview.", {
          point: geoPreviewPoint,
        });
      },
      (error) => {
        if (cancelled) {
          return;
        }

        logSixMinuteWalkFlowDebug("warn", "Pre-start location preview failed.", {
          code: error?.code || null,
          message: error?.message || null,
        });
        setLocationError(error?.code || "permission");
      },
      {
        enableHighAccuracy: true,
        maximumAge: 0,
        timeout: 15000,
      },
    );

    return () => {
      cancelled = true;
    };
  }, [
    assessment?.assessmentId,
    hasPreMeasurement,
    hasStarted,
    resumePostRecording,
  ]);

  useEffect(() => {
    if (!hasPreMeasurement || !hasStarted) {
      releaseWakeLock();
      return undefined;
    }

    requestWakeLock();

    if (typeof document === "undefined") {
      return () => {
        releaseWakeLock();
      };
    }

    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        requestWakeLock();
      } else {
        releaseWakeLock();
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      releaseWakeLock();
    };
  }, [hasPreMeasurement, hasStarted, releaseWakeLock, requestWakeLock]);

  const cancelAssessment = useCallback(() => {
    navigate(SIX_MINUTE_WALK_BASE_PATH, { replace: true });
  }, [navigate]);

  return useMemo(
    () => ({
      geoRouteTracePreview,
      hasPreMeasurement,
      hasReturnedToStart,
      hasStarted,
      highlightedDistanceLabel,
      isReturning,
      isTimedComplete,
      locationError,
      progressPercent,
      resumeElapsedSeconds,
      routeTracePreview,
      status,
      timerValue,
      startWalk,
      proceedToPostRecording,
      cancelAssessment,
    }),
    [
      cancelAssessment,
      geoRouteTracePreview,
      hasPreMeasurement,
      hasReturnedToStart,
      hasStarted,
      highlightedDistanceLabel,
      isReturning,
      isTimedComplete,
      locationError,
      progressPercent,
      resumeElapsedSeconds,
      routeTracePreview,
      startWalk,
      status,
      timerValue,
      proceedToPostRecording,
    ],
  );
}
